// Shim for bootstrap-vue's $bvModal API → bootstrap-vue-next
// Uses a registry since BVN teleports modals to body, making DOM lookup unreliable.
// msgBoxConfirm / msgBoxOk dynamically mount a real BModal so they look and
// behave like the rest of the app's modals (header, footer, OK/Cancel buttons)
// instead of falling back to native window.confirm/alert.

import { createApp, h, ref, nextTick } from 'vue';
import { BModal, createBootstrap } from 'bootstrap-vue-next';

const modalRegistry = {};

// Inject the styles once so dynamically-mounted msgBox modals match p-modal's chrome
// (no divider lines, larger title font).
let stylesInjected = false;
function ensureMsgBoxStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.textContent = `
        .p-msg-box-modal .modal-header {
            border-bottom: 0 none;
            padding-bottom: 0;
        }
        .p-msg-box-modal .modal-footer {
            border-top: 0 none;
        }
        .p-msg-box-modal .modal-title {
            font-size: 1.25rem;
            margin: 0;
        }
    `;
    document.head.appendChild(style);
}

export function registerModal(id, instance) {
    modalRegistry[id] = instance;
}

export function unregisterModal(id) {
    delete modalRegistry[id];
}

function showMessageBox(message, opts = {}, withCancel = true) {
    ensureMsgBoxStyles();
    return new Promise(resolve => {
        const container = document.createElement('div');
        document.body.appendChild(container);

        const visible = ref(false);
        let result = false;

        const app = createApp({
            mounted() {
                // Defer to next tick so the BModal teleport has a chance to wire up
                // before we flip visible to true (otherwise the open animation can be skipped).
                nextTick(() => {
                    visible.value = true;
                });
            },
            render() {
                const titleText = opts.title || (withCancel ? 'Confirm' : 'Notice');
                return h(
                    BModal,
                    {
                        modelValue: visible.value,
                        'onUpdate:modelValue': v => {
                            visible.value = v;
                        },
                        title: titleText,
                        okTitle: opts.okTitle || 'OK',
                        cancelTitle: opts.cancelTitle || 'Cancel',
                        okVariant: opts.okVariant || 'primary',
                        cancelVariant: opts.cancelVariant || 'outline-primary',
                        size: opts.size || 'md',
                        centered: opts.centered ?? true,
                        okOnly: !withCancel,
                        noCloseOnBackdrop: opts.noCloseOnBackdrop ?? false,
                        noCloseOnEsc: opts.noCloseOnEsc ?? false,
                        modalClass: 'p-msg-box-modal',
                        onOk: () => {
                            result = true;
                        },
                        onCancel: () => {
                            result = false;
                        },
                        onHidden: () => {
                            // Defer unmount so BModal can finish its hide transition cleanly.
                            setTimeout(() => {
                                app.unmount();
                                container.remove();
                                resolve(result);
                            }, 0);
                        }
                    },
                    {
                        header: ({ close }) => [
                            h('h3', { class: 'modal-title' }, titleText),
                            h('button', {
                                type: 'button',
                                class: 'btn-close',
                                'aria-label': 'Close',
                                onClick: () => close()
                            })
                        ],
                        default: () =>
                            h(
                                'div',
                                { style: 'white-space: pre-wrap;' },
                                String(message ?? '')
                            )
                    }
                );
            }
        });

        app.use(createBootstrap());
        app.mount(container);
    });
}

export default {
    install(app) {
        const bvModal = {
            show(id) {
                const modal = modalRegistry[id];
                if (modal?.show) {
                    modal.show();
                }
            },
            hide(id) {
                const modal = modalRegistry[id];
                if (modal?.hide) {
                    modal.hide();
                }
            },
            msgBoxConfirm(message, opts) {
                return showMessageBox(message, opts, true);
            },
            msgBoxOk(message, opts) {
                return showMessageBox(message, opts, false).then(() => true);
            }
        };

        app.config.globalProperties.$bvModal = bvModal;
    }
};
